import { PrimaryClientType } from 'api/types/entity';
import {
  OrderMarketType,
  OrderOperationType,
  OrderSettlementConditionType,
  OrderStatusType,
  OrderTransactionVenueType,
} from 'api/types/entity/order';
import { applySchema, yesOrNotSelectValidation } from 'helpers';
import {
  TypedForm,
  FormErrors as FormErrorsBase,
  ObjectSchema,
  date,
  number,
  object,
  selectSchema,
  string,
  array,
} from 'libs/form';
import { TFunc } from 'libs/i18n';
import { localFileScheme } from 'modules/client/common/helpers';

import { testEndDate, testStartDate } from './helpers';
import { getSecuritiesDepositSchema } from './schemas/securitiesDepositSchema';
import { getSecuritiesPurchaseSchema } from './schemas/securitiesPurchaseSchema';
import { getSecuritiesSaleSchemas } from './schemas/securitiesSaleSchemas';
import { getSecuritiesWithdrawalSchema } from './schemas/securitiesWithdrawalSchema';
import { OrderFormType } from './type';

export const MAX_FILES = 10;
export const CODE_LENGTH = 6;

const getDefaultSchema = (
  orderOperationType: OrderOperationType,
  mode: 'edit' | 'create'
) => {
  const isPurchaseOrSale =
    orderOperationType === OrderOperationType.SecuritiesPurchase ||
    orderOperationType === OrderOperationType.SecuritiesSale;

  return object({
    fmNumber: string().max(50),
    marketType: selectSchema(Object.values(OrderMarketType))
      .default(null)
      .when({
        is: () => isPurchaseOrSale && mode === 'edit',
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.nullable(),
      }),
    transactionVenue: selectSchema(Object.values(OrderTransactionVenueType))
      .default(null)
      .when({
        is: () => isPurchaseOrSale && mode === 'edit',
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.nullable(),
      }),
    settlementCondition: selectSchema(
      Object.values(OrderSettlementConditionType)
    )
      .default(null)
      .nullable(),
    manager: object({
      id: string().required(),
      name: string().required(),
    })
      .nullable()
      .default(null),

    hasBrokerRecommendation: yesOrNotSelectValidation.default(null).nullable(),
    comment: string().max(2000).optional().default(''),
    adminDocuments: array().of(localFileScheme.required()).default([]),
  });
};

interface Args {
  orderType: OrderOperationType;
  minEndDate: Date | undefined;
  disabledDates: Date[];
  mode: 'edit' | 'create';
  orderStatus?: OrderStatusType;
}

export const getSchema = (
  t: TFunc,
  { disabledDates, minEndDate, orderType, mode, orderStatus }: Args
): ObjectSchema<OrderFormType> => {
  const defaultSchema = getDefaultSchema(orderType, mode);

  // If order is not in 'AwaitingSigning' status and form is in edit mode, we don't require any fields
  if (orderStatus !== OrderStatusType.AwaitingSigning && mode === 'edit') {
    return defaultSchema as ObjectSchema<OrderFormType>;
  }

  return defaultSchema
    .concat(
      object({
        clientType: selectSchema(Object.values(PrimaryClientType))
          .default(null)
          .when({
            is: () => mode === 'create',
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.nullable(),
          }),
        client: object({
          value: string().required(),
          label: string().required(),
        })
          .default(null)
          .when({
            is: () => mode === 'create',
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.nullable(),
          }),
        startDate: date()
          .nullable()
          .max(new Date())
          .test(
            'startDate',
            t(
              'order.sections.executionPeriodSection.fields.endDate.validationMessage'
            ),
            (value) => testStartDate(value, disabledDates)
          ),
        endDate: date()
          .required()
          .test(
            'endDate',
            t(
              'order.sections.executionPeriodSection.fields.endDate.validationMessage'
            ),
            (value, context) =>
              testEndDate(
                context.parent.startDate as Date | null,
                minEndDate,
                value as Date | null,
                disabledDates
              )
          ),
        executionPeriodInBusinessDays: number(),
      })
    )
    .concat(
      applySchema(
        orderType === OrderOperationType.SecuritiesSale,
        getSecuritiesSaleSchemas(t)
      )
    )
    .concat(
      applySchema(
        orderType === OrderOperationType.SecuritiesPurchase,
        getSecuritiesPurchaseSchema(t)
      )
    )
    .concat(
      applySchema(
        orderType === OrderOperationType.SecuritiesWithdrawal,
        getSecuritiesWithdrawalSchema(t)
      )
    )
    .concat(
      applySchema(
        orderType === OrderOperationType.SecuritiesDeposit,
        getSecuritiesDepositSchema(t)
      )
    );
};

export const { Field, Form, useFormContext, useField, Submit } =
  TypedForm<OrderFormType>();

export type FormErrors = FormErrorsBase<OrderFormType>;

export type { OrderFormType };
